import { useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { readCompanyLogo, useCompanyBranding } from '../../data/queries/use-company-branding';
import { saveCompanyBranding } from '../../data/writers/save-company-branding';
import { selectPhotoSource } from '../../platform/photo-source';
import type { CompanyLogoAction, CompanyLogoMime } from '../../data/database.types';
import {
  describeLogoRefusal,
  logoDataUrl,
  prepareCompanyLogo,
  type PreparedLogo,
} from './company-logo';

/**
 * COMPANY BRANDING — the one section Settings currently owns (M10 / S10-03A).
 *
 * Company name is **required**; logo, email and phone are **optional**
 * (PT §17, as widened by Jerry on 2026-08-18). Name-only branding is valid and
 * produces a perfectly good report header.
 *
 * **NOTHING HERE NAMES CREW LINK SERVICES.** The company is data Brandon types.
 * That is what lets another construction company use Crewlink without a line of
 * this changing, and it is why a fixture is the only place that string may
 * appear.
 *
 * **THE LOGO IS PREPARED BEFORE ANYTHING IS SAVED, AND THAT IS THE SAFETY
 * PROPERTY.** A picked file is decoded, shrunk and re-encoded first; only then
 * does one write replace the profile. **A logo that fails at any of those steps
 * leaves the saved one untouched** — Brandon can never end up with no logo
 * because a replacement went wrong.
 *
 * **THIS FEATURE NEVER TOUCHES STORAGE.** The logo is a small column in the
 * database. The structural guard that restricts Supabase Storage to the photo
 * pipeline is unchanged by this slice, and a logo uploader would have forced it
 * to be loosened.
 */
export function CompanyBrandingSection({ onSaved }: { readonly onSaved: () => void }) {
  const { status, branding, error } = useCompanyBranding();
  const [source] = useState(() => selectPhotoSource());

  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  /**
   * What this save should do with the logo.
   *
   * **`keep` IS THE DEFAULT AND IT IS LOAD-BEARING.** Saving a phone number
   * must not disturb a logo, and a nullable "new logo" value could not express
   * the difference between leaving one alone and removing it.
   */
  const [logoAction, setLogoAction] = useState<CompanyLogoAction>('keep');
  const [pickedLogo, setPickedLogo] = useState<PreparedLogo | null>(null);
  const [savedLogo, setSavedLogo] = useState<{ mime: CompanyLogoMime; base64: string } | null>(
    null,
  );

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Server truth until Brandon types; then his edit, until he saves.
  const nameValue = name ?? branding?.company_name ?? '';
  const emailValue = email ?? branding?.contact_email ?? '';
  const phoneValue = phone ?? branding?.contact_phone ?? '';
  const hasSavedLogo = branding?.logo_mime != null;

  async function showSavedLogo(): Promise<void> {
    // The BYTES are fetched only here, by the one screen that draws them. The
    // ordinary branding read deliberately leaves them on the server.
    if (!branding?.logo_mime) return;
    const base64 = await readCompanyLogo();
    if (base64) setSavedLogo({ mime: branding.logo_mime, base64 });
  }

  async function pickLogo(): Promise<void> {
    setNotice(null);
    setSaved(false);
    setBusy(true);
    try {
      const acquired = await source.pick('library', { multiple: false });
      // An empty result is a CANCEL, not an error — the port's own contract.
      const file = acquired[0];
      if (!file) return;

      const prepared = await prepareCompanyLogo(file.bytes, file.declaredMime);
      if (!prepared.ok) {
        setNotice(describeLogoRefusal(prepared.refusal));
        return;
      }
      // Held, not saved. Nothing on the server has changed yet.
      setPickedLogo(prepared.logo);
      setLogoAction('replace');
    } catch {
      setNotice('That image could not be read. Please choose a different file.');
    } finally {
      setBusy(false);
    }
  }

  async function save(): Promise<void> {
    const client = getSupabaseClient();
    if (!client) {
      setNotice('Crewlink is not connected to a server yet.');
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      const result = await saveCompanyBranding(client, {
        companyName: nameValue,
        action: logoAction,
        logo: logoAction === 'replace' && pickedLogo ? pickedLogo : undefined,
        contactEmail: emailValue.trim() === '' ? null : emailValue,
        contactPhone: phoneValue.trim() === '' ? null : phoneValue,
      });
      if (!result.ok) {
        setNotice(result.message);
        return;
      }
      setSaved(true);
      onSaved();
    } finally {
      setBusy(false);
    }
  }

  const previewLogo = pickedLogo ?? savedLogo;
  const nameIsBlank = nameValue.trim() === '';

  return (
    <section className="settings-section" aria-labelledby="company-branding-heading">
      <h2 id="company-branding-heading" className="cl-screen-title">
        Company branding
      </h2>
      <p className="settings-lead">
        Set this once. Every report can use it, and you can leave it off any report you like.
      </p>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading your company branding…
        </p>
      ) : null}

      {status === 'failed' && error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      <label className="cl-field">
        <span className="cl-label">Company name</span>
        <input
          className="cl-input"
          type="text"
          value={nameValue}
          onChange={(event) => {
            setName(event.target.value);
            setSaved(false);
          }}
          maxLength={120}
          autoComplete="organization"
        />
      </label>

      <div className="cl-field">
        <span className="cl-label">
          Logo <span className="settings-optional">— optional</span>
        </span>

        {/* NO PLACEHOLDER BOX WHEN THERE IS NO LOGO. An empty frame is the
            wasted space this milestone keeps refusing. */}
        {previewLogo ? (
          <span className="settings-logo-preview">
            <img src={logoDataUrl(previewLogo.mime, previewLogo.base64)} alt="Company logo" />
          </span>
        ) : null}

        <div className="settings-logo-actions">
          <button
            type="button"
            className="cl-button cl-button--quiet"
            disabled={busy}
            onClick={() => void pickLogo()}
          >
            {hasSavedLogo || pickedLogo ? 'Change logo' : 'Add logo'}
          </button>

          {hasSavedLogo && !savedLogo && !pickedLogo ? (
            <button
              type="button"
              className="cl-button cl-button--quiet"
              disabled={busy}
              onClick={() => void showSavedLogo()}
            >
              Show current logo
            </button>
          ) : null}

          {(hasSavedLogo || pickedLogo) && logoAction !== 'remove' ? (
            <button
              type="button"
              className="cl-button cl-button--quiet"
              disabled={busy}
              onClick={() => {
                // REMOVE CLEARS THE LOGO AND NOTHING ELSE. Name, email and
                // phone are untouched, here and in the writer.
                setLogoAction('remove');
                setPickedLogo(null);
                setSavedLogo(null);
                setSaved(false);
              }}
            >
              Remove logo
            </button>
          ) : null}
        </div>

        {logoAction === 'remove' ? (
          <p className="settings-lead">Your logo will be removed when you save.</p>
        ) : null}
      </div>

      <label className="cl-field">
        <span className="cl-label">
          Email <span className="settings-optional">— optional</span>
        </span>
        <input
          className="cl-input"
          type="email"
          value={emailValue}
          onChange={(event) => {
            setEmail(event.target.value);
            setSaved(false);
          }}
          maxLength={254}
          autoComplete="email"
        />
      </label>

      <label className="cl-field">
        <span className="cl-label">
          Phone <span className="settings-optional">— optional</span>
        </span>
        <input
          className="cl-input"
          type="tel"
          value={phoneValue}
          onChange={(event) => {
            setPhone(event.target.value);
            setSaved(false);
          }}
          maxLength={40}
          autoComplete="tel"
        />
      </label>

      {notice ? (
        <p className="cl-error" role="alert">
          {notice}
        </p>
      ) : null}

      {/* EXPLICIT SAVE, matching every other form in Crewlink. Autosaving a
          company identity while he is mid-word would be a different product. */}
      <button
        type="button"
        className="cl-button settings-save"
        disabled={busy || nameIsBlank}
        onClick={() => void save()}
      >
        {busy ? 'Saving…' : 'Save'}
      </button>

      {saved ? (
        <p className="settings-saved" role="status">
          Company branding saved.
        </p>
      ) : null}
    </section>
  );
}
